import { ChangeDetectionStrategy, Component, HostListener, computed, effect, inject, signal, untracked, viewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { SlidePanelComponent } from '../slide-panel/slide-panel.component';
import { ShortcutListComponent } from './shortcut-list.component';
import { OPEN_SEARCH_SHORTCUT, SHORTCUT_GROUPS, orderGroupsForSurface } from './shortcut-manifest';
import { KeyboardShortcutRegistryService } from '../../services/keyboard-shortcut-registry.service';
import { SlidePanelService } from '../../services/slide-panel.service';
import { AuthService } from '../../services/auth.service';
import { FilePreviewService } from '../../services/file-preview.service';
import { ArticleOverlayPresence } from '../article-preview';
import { LightboxPresence } from '../public-media-lightbox/lightbox-presence.service';
import { TemporalContextService } from '../../services/temporal-context.service';
import { CommandPaletteService } from '../command-palette/command-palette.service';
import { TourService } from '../../services/tour.service';
import { isTypingTarget, topmostDialogMaskZ } from '../../utils/keyboard.utils';
import { claimEscape, isEscapeClaimed } from '@branduals/dialog-guard';

/**
 * The global keyboard-shortcuts reference: a floating companion panel a member opens with `?` (or
 * the topbar's profile item) and keeps open while trying the keys it lists — glance-and-try is the
 * point, so the panel deliberately PERSISTS across outside clicks (`closeOnClickOutside` off, the
 * behavior the reference tools this audience knows ship for their own shortcut panels). Its close
 * paths are the panel's X, the `?` toggle, this component's own Escape while the reference is the
 * topmost layer, and a panel opening THROUGH the panel service (the reference yields to working
 * panels but never evicts one — it opens bare, outside the mutex).
 *
 * `helpOpen` (on the shortcut registry) stays the one open/closed signal — the `?` key, the topbar
 * item and this panel all speak through it, mirrored two-way here via the panel's `(closed)`.
 */
@Component({
  selector: 'app-shortcut-help-panel',
  standalone: true,
  imports: [TranslateModule, SlidePanelComponent, ShortcutListComponent],
  templateUrl: './shortcut-help-panel.component.html',
  styleUrl: './shortcut-help-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShortcutHelpPanelComponent {
  private readonly registry = inject(KeyboardShortcutRegistryService);
  private readonly slidePanelService = inject(SlidePanelService);
  private readonly auth = inject(AuthService);
  private readonly filePreview = inject(FilePreviewService);
  private readonly articleOverlay = inject(ArticleOverlayPresence);
  private readonly lightbox = inject(LightboxPresence);
  private readonly temporal = inject(TemporalContextService);
  private readonly commandPalette = inject(CommandPaletteService);
  private readonly tour = inject(TourService);

  private readonly panel = viewChild(SlidePanelComponent);

  /** The service slot's last seen value — the yield effect keys on transitions, not standing state. */
  private lastSeenPanel: SlidePanelComponent | null = null;

  /** Current-surface-first: with the file viewer open, its group leads and the active workflow
   *  context's group (voting, deciding) follows it (presentation only). In Time travel the
   *  Open-search row is dropped by identity — the palette refuses those keys in that mode, and
   *  the reference never advertises a dead key. */
  protected readonly groups = computed(() => {
    const ordered = orderGroupsForSurface(
      SHORTCUT_GROUPS, this.filePreview.isOpen(), this.filePreview.context()?.contextKey ?? null);
    if (!this.temporal.active()) return ordered;
    return ordered.map(g => g.rows.includes(OPEN_SEARCH_SHORTCUT)
      ? { ...g, rows: g.rows.filter(row => row !== OPEN_SEARCH_SHORTCUT) }
      : g);
  });

  /**
   * The topmost NON-PROMPT dialog-mask z + 5 while the reference is open; null with no mask.
   * Fed by the mutation observers below — a LIVE value, so a modal opened AFTER the reference
   * re-lifts it (discard prompts stay excluded: their 1310 rung must keep painting above).
   */
  private readonly liveDialogRung = signal<number | null>(null);

  /** The reference lifts above whatever it floats over: the file preview OR the anonymous media
   *  lightbox (both paint at 1200 → 1250; the lightbox is read from its class-wide presence home,
   *  because it mounts per share/portal page — a signed-in member pressing ? over a share page's
   *  photo had opened the reference INVISIBLY under the scrim, where it still ate the Escape), the
   *  article overlay (1100 → 1150 — read from the class-wide presence home, because the overlay
   *  service is route-provided on the share surfaces and a per-instance read would miss those),
   *  or the topmost live dialog mask (+5 — 1105 over a default 1100 host, 1305 over a 1300-lifted
   *  one, always below the 1310 prompts in CSS-governed states). Undefined = the panel's own
   *  rendered default. The containment roster holds every inert full-screen host to a read here. */
  protected readonly panelZIndex = computed(() => {
    const signalRung = this.filePreview.isOpen() || this.lightbox.anyOpen() ? 1250
      : this.articleOverlay.anyOpen() ? 1150 : 0;
    const live = this.liveDialogRung() ?? 0;
    const z = Math.max(signalRung, live);
    return z === 0 ? undefined : z;
  });

  constructor() {
    // Two-way mirror: the signal drives the panel through a BARE open — the reference must never
    // evict a member's working panel (drafts live in those), so it stays outside the service
    // mutex and accepts that two floating panels may overlap.
    effect(() => {
      const open = this.registry.helpOpen();
      const panel = this.panel();
      if (!panel) return;
      if (open && !panel.isOpen()) panel.open();
      else if (!open && panel.isOpen()) panel.close();
    });

    // Victim-only: the reference still YIELDS when another panel opens through the service while
    // it is up. The yield keys on the TRANSITION to a newly-opened panel (the slot is an honest
    // currently-open-registered-panel seam — a panel closing itself clears it, so a same-panel
    // re-open is a fresh transition too); a panel that was already open when the reference
    // opened is not a fresh gesture and never closes it.
    effect(() => {
      const current = this.slidePanelService.currentPanel();
      const previous = this.lastSeenPanel;
      this.lastSeenPanel = current;
      if (current === null || current === previous) return;
      const panel = untracked(() => this.panel());
      if (!panel || current === (panel as unknown)) return;
      if (untracked(() => this.registry.helpOpen())) this.registry.helpOpen.set(false);
    });

    // The live dialog rung: connected only while the reference is open. One structural observer
    // watches masks appear and leave (childList + subtree — the app leaves PrimeNG's
    // overlayAppendTo at its default 'self', so masks render INSIDE their dialog hosts, never as
    // body children) and one attribute observer per rendered mask catches ZIndexUtils' inline z,
    // which lands on the ENTER MOTION a tick after insertion. The connect recomputes FIRST: a
    // reference opened OVER an already-open dialog must lift at open (an observer fires nothing
    // at observe() time).
    effect(onCleanup => {
      if (!this.registry.helpOpen()) { this.liveDialogRung.set(null); return; }
      const attrObservers: MutationObserver[] = [];
      const recompute = () => {
        const max = topmostDialogMaskZ({ excludePrompts: true });
        this.liveDialogRung.set(max === null ? null : max + 5);
      };
      const watchMasks = () => {
        for (const o of attrObservers) o.disconnect();
        attrObservers.length = 0;
        for (const mask of Array.from(document.querySelectorAll('.p-dialog-mask'))) {
          const o = new MutationObserver(recompute);
          o.observe(mask, { attributes: true, attributeFilter: ['style'] });
          attrObservers.push(o);
        }
      };
      const structural = new MutationObserver(() => { watchMasks(); recompute(); });
      recompute();
      watchMasks();
      structural.observe(document.body, { childList: true, subtree: true });
      onCleanup(() => {
        structural.disconnect();
        for (const o of attrObservers) o.disconnect();
        this.liveDialogRung.set(null);
      });
    });
  }

  protected onClosed(): void {
    this.registry.helpOpen.set(false);
  }

  /**
   * The one `?` owner, plus the reference's own Escape while it is the topmost layer.
   *
   * `?` guards: never while typing (the shared predicate), never during an IME composition, and
   * only for a SIGNED-IN person — the same population the topbar's shortcuts item reaches (a
   * read-only reference is identity chrome, not member chrome, so demo visitors and signed-in
   * non-members keep it; the surfaces its keys act on still gate themselves). Modified presses
   * (Ctrl/⌘/Alt) belong to other owners.
   *
   * Escape claims the press only when the reference is genuinely on top: it stands down during a
   * composition, for a genuinely-claimed press (the shared `isEscapeClaimed` predicate — it sees
   * through a widget's HOLLOW claim, where a PrimeNG field prevents an Escape it did nothing
   * with, so the key still works from inside such a field), while the command palette is open
   * (its own later-registered listener closes it), while a tour runs (Escape is the tour's
   * chrome), and beneath any dialog mask at or above the panel's own z — a TIE yields to the
   * mask, which paints on top by DOM order. When it claims (`claimEscape`, which also clears a
   * hollow tag), it stops immediate propagation so PrimeNG's dialog listener, the escape
   * directive and every sibling panel stand down for that press.
   */
  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.onEscape(event);
      return;
    }
    if (event.key !== '?' || event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.isComposing || isTypingTarget(event)) return;
    if (!this.auth.signedIn()) return;
    event.preventDefault();
    this.registry.helpOpen.update(open => !open);
  }

  private onEscape(event: KeyboardEvent): void {
    if (!this.registry.helpOpen()) return;
    if (event.isComposing || isEscapeClaimed(event)) return;
    if (this.commandPalette.isOpen()) return;
    if (this.tour.activeTour()) return;
    const topMask = topmostDialogMaskZ();
    const ownZ = this.panelZIndex() ?? 1000;
    if (topMask !== null && topMask >= ownZ) return;
    this.registry.helpOpen.set(false);
    claimEscape(event);
    event.stopImmediatePropagation();
  }
}
